/**
 * **Zone clearance** — the pure cell-by-cell read of *"has the ground inside this zone
 * been flattened?"*, and the only sitrep primitive that reads **terrain** at all.
 *
 * The six printed sitreps score by occupancy (`./zones`'s `unitsInZone`/`controlOf`)
 * or by tracking one objective combatant (`./predicates/extraction`). The homebrew
 * seventh, `clear`, scores the map layers themselves: a size-4 terraforming rig
 * bulldozes a landing zone flat, and the encounter is won when nothing is left
 * standing in it.
 *
 * ## What "clear" means (three sources of obstruction, all read live)
 *
 *   1. **Cell terrain** — any {@link TerrainProperties} flag that makes the cell something
 *      other than open, flat ground: `blocksMovement`/`blocksFlight`/`blocksSight`,
 *      `dangerous`, non-`"none"` `cover`, or difficult terrain (`moveCost > 1`).
 *      Read flag-by-flag rather than "is there a map entry at all" so an
 *      unsparsified all-default entry can't read as rubble.
 *   2. **Interior edge terrain** — a wall/low wall/fence on an edge **between two
 *      cells that are both inside the zone**. Both cells count as obstructed (the
 *      wall stands on the boundary between them; neither is flat while it's there).
 *   3. **Standing objects** — a placed, {@link isAble}, {@link contributesToTerrain}
 *      combatant (an `object`/`deployable` carrying an `objectProfile`) whose
 *      footprint covers the cell. The rig's sweep removes a map asset outright and its
 *      run-over destroys a plain object; either way it stops obstructing immediately — the same live-query posture
 *      `objectContributions.ts` takes for sight/cover.
 *
 * ## Three deliberate exclusions — each of these would break the encounter
 *
 * - **Perimeter edges are NOT read** (only edges with both cells inside the zone).
 *   A zone drawn against a map wall, or one the GM deliberately fenced, would
 *   otherwise be permanently unclearable — the rig can't erase an edge whose far
 *   side is off the objective.
 * - **Characters do NOT obstruct.** Enemy waves are endless until the zone is clear,
 *   so if bodies standing in the zone counted, the win condition would be unreachable
 *   by construction: the GM keeps spawning hostiles, and the predicate is the only
 *   clock. Only terrain-like things (1–3 above) count.
 * - **A zero-cell zone is never clear.** `total === 0` (an empty rect list, or a
 *   hex-mode encounter — see below) reports `clear: 0` and callers gate on
 *   `total > 0`, so the vacuous "every cell of no cells is flat" can't hand out a
 *   win.
 *
 * ## Hex encounters
 *
 * Zone geometry is a `2d-square` concept — {@link SitrepZone.rects} are `RoomRect`s
 * in square columns/rows, exactly as `./zones` documents. On a `2d-hex`/`3d-hex`
 * encounter this reports every zone as `total: 0` rather than throwing: authoring a
 * sitrep on a hex board is a GM-level footgun, not a reason to crash a pure query,
 * and `total: 0` can never be mistaken for a win.
 */

import {
  cellProperties,
  contributesToTerrain,
  edgeMapKey,
  encounterGridMode,
  isAble,
  rectContainsCell,
  squareCell,
  worldFor,
  type TerrainProperties,
  type EdgeTerrain,
  type EncounterState,
  type SitrepConfig,
  type SitrepZone,
} from "@lancer-vtt/domain";
import { footprintCells } from "../effects/spatialAnswerer";

/** One zone's clearance tally. `clear + obstructed === total`, always. */
export type ZoneClearance = {
  zoneId: string;
  /** Cells inside the zone's rects (deduped — overlapping rects count a cell once). */
  total: number;
  /** Cells that are flat, open ground by all three readings above. */
  clear: number;
  /** Cells still carrying terrain, an interior wall, or a standing object. */
  obstructed: number;
  /**
   * **Which** cells are still obstructed, rect-by-rect in row-major order — so a surface can show
   * the GM and the rig's driver *where* to drive, not just how much is left. This is
   * the whole reason the board render is worth having: "18 cells to go" doesn't tell
   * anyone where they are. Derived, never stored; empty when the zone reads clear.
   */
  obstructedCells: { x: number; y: number }[];
};

/** The whole sitrep's clearance — every `clear`-kind zone, plus the summed totals. */
export type SitrepClearance = {
  total: number;
  clear: number;
  obstructed: number;
  zones: ZoneClearance[];
};

/**
 * Clearance as a whole-number percent, `0` for a zero-cell objective. Shared by every
 * surface that shows progress (the CLI's `sitrep status`, COMBAT.app's sitrep HUD) so
 * the GM's terminal and the players' screens can never disagree by a rounding rule.
 * Rounds **down**, so `99%` never means "actually finished" — only a literal `100`
 * corresponds to the win.
 */
export function clearancePercent(c: Pick<SitrepClearance, "total" | "clear">): number {
  return c.total === 0 ? 0 : Math.floor((c.clear / c.total) * 100);
}

/** Whether a cell's terrain properties make it something other than flat, open ground. */
function terrainObstructs(t: TerrainProperties | undefined): boolean {
  if (!t) return false;
  return (
    t.blocksMovement === true ||
    t.blocksFlight === true ||
    t.blocksSight === true ||
    t.dangerous === true ||
    (t.cover !== undefined && t.cover !== "none") ||
    (typeof t.moveCost === "number" && t.moveCost > 1)
  );
}

/** Whether an edge carries a wall/low wall/fence — anything standing on the boundary. */
function edgeObstructs(e: EdgeTerrain | undefined): boolean {
  if (!e) return false;
  return (
    e.blocksMovement === true ||
    e.blocksFlight === true ||
    e.blocksSight === true ||
    (e.cover !== undefined && e.cover !== "none")
  );
}

/** Every distinct `{x,y}` inside `zone`'s rects (overlapping rects deduped). */
function zoneCells(zone: SitrepZone): { x: number; y: number }[] {
  const seen = new Set<string>();
  const out: { x: number; y: number }[] = [];
  for (const r of zone.rects) {
    for (let y = r.y; y < r.y + r.h; y++) {
      for (let x = r.x; x < r.x + r.w; x++) {
        const k = `${x},${y}`;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push({ x, y });
      }
    }
  }
  return out;
}

/**
 * One zone's clearance tally. Pure; reads `state.terrain`, `state.edges` and
 * `state.combatants` live (nothing is cached on `EncounterState`), so the answer
 * updates the instant a sweep lands or an object is destroyed.
 */
export function zoneClearance(state: EncounterState, zone: SitrepZone): ZoneClearance {
  if (encounterGridMode(state) !== "2d-square") {
    return { zoneId: zone.id, total: 0, clear: 0, obstructed: 0, obstructedCells: [] };
  }

  const cells = zoneCells(zone);
  const inZone = new Set(cells.map((c) => `${c.x},${c.y}`));
  const world = worldFor(state);

  // The cells covered by every placed, live, profile-carrying object — derived once
  // over the roster rather than re-scanned per zone cell.
  const objectCells = new Set<string>();
  for (const c of Object.values(state.combatants)) {
    if (!contributesToTerrain(c) || c.pos == null || !isAble(c)) continue;
    for (const cell of footprintCells(world, c)) {
      if ("x" in cell && "y" in cell) objectCells.add(`${cell.x},${cell.y}`);
    }
  }

  let clear = 0;
  const obstructedCells: { x: number; y: number }[] = [];
  for (const { x, y } of cells) {
    const here = squareCell(x, y);
    let obstructed = terrainObstructs(cellProperties(world, state.terrain, here)) || objectCells.has(`${x},${y}`);

    // Interior edges only — the four orthogonal neighbours that are ALSO in the zone.
    if (!obstructed) {
      for (const [nx, ny] of [
        [x + 1, y],
        [x - 1, y],
        [x, y + 1],
        [x, y - 1],
      ] as const) {
        if (!inZone.has(`${nx},${ny}`)) continue;
        if (edgeObstructs(state.edges[edgeMapKey(world, here, squareCell(nx, ny))])) {
          obstructed = true;
          break;
        }
      }
    }

    if (obstructed) obstructedCells.push({ x, y });
    else clear++;
  }

  return { zoneId: zone.id, total: cells.length, clear, obstructed: obstructedCells.length, obstructedCells };
}

/**
 * The whole sitrep's clearance across its `clear`-kind zones — the number the table
 * reads (`N/M cells · P% clear`) and the number {@link "./predicates/clear"
 * clearStatus} decides on. Zones of any other kind are ignored.
 */
export function sitrepClearance(state: EncounterState, sitrep: SitrepConfig): SitrepClearance {
  const zones = sitrep.zones.filter((z) => z.kind === "clear").map((z) => zoneClearance(state, z));
  return {
    total: zones.reduce((n, z) => n + z.total, 0),
    clear: zones.reduce((n, z) => n + z.clear, 0),
    obstructed: zones.reduce((n, z) => n + z.obstructed, 0),
    zones,
  };
}
